import { useId, useRef, useState } from 'react'
import { Button, Spinner } from '@heroui/react'

import { PlusIcon, TrashIcon } from '@/components/ui/icons'
import type { FileRef } from '@/lib/api/common'
import { errorMessage } from '@/lib/api/api-error'

import { useFileUpload } from '../hooks'
import { imageFileError } from '../image-validation'

interface ImageThumbFieldProps {
  /** Names the picker for a screen reader — there is no visible label in a grid cell. */
  label: string
  value: FileRef | null
  onChange: (file: FileRef | null) => void
  isDisabled?: boolean
}

/**
 * A picture the size of a grid cell: click to upload or replace, with a remove
 * button over the corner once there is one. For a row of a table, where
 * `ImageField`'s preview and two buttons would be taller than the row.
 *
 * A failed upload is shown as the tile's tooltip and border rather than a line of
 * text: a message under it would push the whole row taller than its neighbours.
 */
export function ImageThumbField({ label, value, onChange, isDisabled }: ImageThumbFieldProps) {
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const { upload, isUploading, error } = useFileUpload()
  const [localError, setLocalError] = useState<string | null>(null)

  async function handlePick(file: File | undefined) {
    if (!file) return

    setLocalError(null)

    const invalid = imageFileError(file)

    if (invalid) {
      setLocalError(invalid)
      return
    }

    try {
      onChange(await upload(file))
    } catch {
      // `error` off the mutation carries the server's wording; nothing to add.
    } finally {
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const message = localError ?? (error ? errorMessage(error, 'Upload failed.') : null)

  return (
    <div className="relative size-9 shrink-0">
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="image/*"
        aria-label={label}
        className="sr-only"
        disabled={isDisabled || isUploading}
        onChange={(event) => void handlePick(event.target.files?.[0])}
      />

      <Button
        variant="ghost"
        isIconOnly
        aria-label={value ? `Replace ${label}` : `Upload ${label}`}
        isDisabled={isDisabled || isUploading}
        onPress={() => inputRef.current?.click()}
        className={`bg-surface size-9 min-w-0 overflow-hidden rounded-lg border p-0 ${
          message ? 'border-danger' : 'border-border border-dashed'
        }`}
      >
        {isUploading ? (
          <Spinner size="sm" />
        ) : value ? (
          <img src={value.url} alt="" title={message ?? undefined} className="size-full object-cover" />
        ) : (
          <span title={message ?? undefined}>
            <PlusIcon width={14} height={14} />
          </span>
        )}
      </Button>

      {value && !isUploading && (
        <Button
          variant="ghost"
          isIconOnly
          size="sm"
          aria-label={`Remove ${label}`}
          isDisabled={isDisabled}
          onPress={() => {
            setLocalError(null)
            onChange(null)
          }}
          className="bg-background text-muted hover:text-danger border-border absolute -end-1.5 -top-1.5 size-4 min-w-0 rounded-full border p-0"
        >
          <TrashIcon width={9} height={9} />
        </Button>
      )}
    </div>
  )
}
